import dayjs from "dayjs";
import { Pencil } from "lucide-react";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import {
  RouteComponentProps,
  withRouter,
  NavLink,
  useHistory,
} from "react-router-dom";

import useLatestKeywordAnalysis from "./hooks/useLatestKeywordAnalysis";
import useLocation from "./hooks/useLocation";
import useLocationLocalPostsList from "./hooks/useLocationLocalPostsList";
import useLocationProjects from "./hooks/useLocationProjects";
import useLocationRecommendations from "./hooks/useLocationRecommendations";
import useRefreshLocationData, {
  RefreshType,
} from "./hooks/useRefreshLocationData";
import { DemoBadge } from "../../components/Common/DemoBadge";
import { OptionalRender } from "../../components/Common/OptionalRender";
import { PageTitle } from "../../components/Common/PageTitle";
import RecentPostList, {
  RecentMediaListContext,
} from "../../components/DashboardComponents/RecentPostsList";
import { ActivityLog } from "../../components/FacilityDetailsComponents/ActivityLog";
import { FacilityDetailsAnalysis } from "../../components/FacilityDetailsComponents/Analysis";
import { BasicChecklist } from "../../components/FacilityDetailsComponents/BasicChecklist";
import { BasicProfile } from "../../components/FacilityDetailsComponents/BasicProfile";
import { CategoryAutocompleter } from "../../components/FacilityDetailsComponents/EditProfileForm/CategorySection/GbpCategoriesInput/CategoryAutocompleter";
import { EditProfileFormSectionKeys } from "../../components/FacilityDetailsComponents/EditProfileForm/const";
import ExternalIntegrations from "../../components/FacilityDetailsComponents/ExternalIntegrations";
import { FacilityDataDownload } from "../../components/FacilityDetailsComponents/FacilityDataDownload";
import { ImportantMetrics } from "../../components/FacilityDetailsComponents/ImportantMetrics";
import { KeywordAnalysisCard } from "../../components/FacilityDetailsComponents/KeywordAnalysisCard";
import LocationKeywordsChart from "../../components/FacilityDetailsComponents/LocationKeywordsChart";
import { MediaCatalogueSection } from "../../components/FacilityDetailsComponents/MediaCatalogueSection";
import { ModalCreateLocalPost } from "../../components/FacilityDetailsComponents/ModalCreateLocalPost";
import { ModalEditProfile } from "../../components/FacilityDetailsComponents/ModalEditProfile";
import ModalUnRepliedReviews from "../../components/FacilityDetailsComponents/ModalUnRepliedReviews";
import { OnboardingBanner } from "../../components/FacilityDetailsComponents/OnboardingBanner";
import { PickupFields } from "../../components/FacilityDetailsComponents/PickupFields";
import { RecentMediaUploadsSection } from "../../components/FacilityDetailsComponents/RecentMediaUploadsSection";
import { RecentReviewsSection } from "../../components/FacilityDetailsComponents/RecentReviewsSection";
import { ReviewRequestPrint } from "../../components/FacilityDetailsComponents/ReviewRequestPrint";
import { ReviewSummary } from "../../components/FacilityDetailsComponents/ReviewSummary";
import { Suggestions } from "../../components/FacilityDetailsComponents/Suggestions";
import { Summary } from "../../components/FacilityDetailsComponents/Summary";
import { InternalToolsContextProvider } from "../../components/InternalTools/InternalToolsContextProvider";
import { InternalToolEnum } from "../../components/InternalTools/InternalToolsDictionaryAndTypes";
import { ScoreItem } from "../../components/ScoreItem";
import { ConfirmCloseContextProvider } from "../../contexts/confirmClose";
import { useUpdateImpersonatedOrganizationContext } from "../../hooks/organizationImpersonation";
import { useMe } from "../../hooks/queries";
import { canAccessOrganization } from "../../hooks/validateUserOrganization";
import { AccountData, AccountType } from "../../models/AccountData";
import { FeatureUseLimitCalculator } from "../../models/featureUseLimits/FeatureUseLimitsCalculator";
import {
  FeatureUseStatus,
  LocFeatures,
  OrgFeatures,
} from "../../models/featureUseLimits/FeatureUseLimitsData.const";
import { ProjectLocationWithProject } from "../../models/ProjectData";
import {
  SearchKeywordParams,
  SearchKeywordResponse,
} from "../../models/SearchKeywordData";
import DataSvc from "../../services/dataSvc";
import SendMessageScreens from "../SendMessageScreens";

import "./styles.scss";

const produceBackLink = (organizationId: string, me?: AccountData) => {
  if (organizationId) {
    return `/locations/organization/${organizationId}`;
  } else if (me?.type === AccountType.ADMIN) {
    return `/admin/locations`;
  }

  return `/locations`;
};

const internalToolsInUse = [InternalToolEnum.GetReviews];
interface MatchParams {
  locationId?: string;
  organizationId?: string;
}

type IFacilityDetailsPageProps = RouteComponentProps<MatchParams>;

const FacilityDetailsPage: React.FunctionComponent<
  IFacilityDetailsPageProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityDetailsPage.${key}`);

  const { data: me } = useMe();
  const history = useHistory();
  const internalToolsRef = useRef<HTMLDivElement>(null);

  const locationId = props.match.params.locationId ?? "";
  const organizationId = props.match.params.organizationId ?? "";

  const [showSendMessageScreens, setShowSendMessageScreens] = useState(false);

  const [shownModalCreateLocalPost, setShownModalCreateLocalPost] =
    useState<boolean>(false);
  const [modalEditProfileSectionKey, setModalEditProfileSectionKey] = useState<
    EditProfileFormSectionKeys | null | undefined
  >(undefined);
  const [showUnRepliedReviews, setShowUnRepliedReviews] =
    useState<boolean>(false);

  const [keywordData, setKeywordData] = useState<SearchKeywordResponse>([]);
  const [keywordDateRange, setKeywordDateRange] = useState<{
    startMonth: number;
    startYear: number;
    endMonth: number;
    endYear: number;
  }>(() => {
    const today = new Date();
    const endYear = today.getFullYear();
    const endMonth = today.getMonth() + 1;

    let startMonth = endMonth - 6;
    let startYear = endYear;

    if (startMonth <= 0) {
      startMonth += 12;
      startYear -= 1;
    }

    return {
      startMonth,
      startYear,
      endMonth,
      endYear,
    };
  });

  const { data: locationData } = useLocation(locationId);

  const locationName = locationData?.name;

  const { data: latestKeywordAnalysis, loading: keywordAnalysisLoading } =
    useLatestKeywordAnalysis(locationId);

  const refresh = useRefreshLocationData(locationId);

  const updateImpersonatedOrganizationContext =
    useUpdateImpersonatedOrganizationContext();
  useEffect(() => {
    if (!me || !locationId) {
      return;
    }

    if (me.type === AccountType.LOC_ADMIN && !!organizationId) {
      history.push(`/location/${locationId}`);
    } else if (
      me.type === AccountType.ORG_ADMIN &&
      !canAccessOrganization(organizationId, me)
    ) {
      history.push(
        `/location/${locationId}/organization/${me?.organization?.id}`
      );
    } else if (me.type === AccountType.ADMIN) {
      updateImpersonatedOrganizationContext(organizationId);
    }

    return () => {
      updateImpersonatedOrganizationContext(null);
    };
  }, [
    locationId,
    organizationId,
    me,
    history,
    updateImpersonatedOrganizationContext,
  ]);

  useEffect(() => {
    // Start loading gbp categories from API optimistically
    CategoryAutocompleter.initialize();
  }, []);

  const profileViewsChangeRatio: number | undefined = useMemo(() => {
    if (
      !locationData ||
      !locationData.stat ||
      !locationData.stat.prev_profile_views ||
      !locationData.stat.profile_views
    ) {
      return undefined;
    }
    return (
      ((locationData.stat.profile_views -
        locationData.stat.prev_profile_views) /
        locationData.stat.prev_profile_views) *
      100
    );
  }, [locationData]);

  const fetchSearchKeywordData = useCallback(async () => {
    if (!locationId) return;

    try {
      const params: SearchKeywordParams = {
        location_id: [Number(locationId)],
        start_month: keywordDateRange.startMonth,
        start_year: keywordDateRange.startYear,
        end_month: keywordDateRange.endMonth,
        end_year: keywordDateRange.endYear,
      };

      const response = await DataSvc.getSearchKeyword(params);
      setKeywordData(response);
    } catch (error) {
      setKeywordData([]);
    }
  }, [locationId, keywordDateRange]);

  const handleKeywordDateRangeChange = useCallback(
    (fromDate: string, toDate: string) => {
      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);

      setKeywordDateRange({
        startMonth: fromDateObj.getMonth() + 1,
        startYear: fromDateObj.getFullYear(),
        endMonth: toDateObj.getMonth() + 1,
        endYear: toDateObj.getFullYear(),
      });
    },
    []
  );

  useEffect(() => {
    fetchSearchKeywordData();
  }, [fetchSearchKeywordData]);

  const calculateInitialDateRange = useMemo(() => {
    const startDate = dayjs()
      .year(keywordDateRange.startYear)
      .month(keywordDateRange.startMonth - 1)
      .startOf("month")
      .utc(true)
      .format("YYYY-MM-DD");

    const endDate = dayjs()
      .year(keywordDateRange.endYear)
      .month(keywordDateRange.endMonth - 1)
      .endOf("month")
      .utc(true)
      .format("YYYY-MM-DD");

    return {
      fromDate: startDate,
      toDate: endDate,
    };
  }, [keywordDateRange]);

  const gbpLocIdNum =
    locationData?.gbp_location_id.replace("locations/", "") || undefined;

  const isOrgUser = () => {
    return me?.type === AccountType.ORG_ADMIN;
  };

  const canCreateLocalPost = useMemo(() => {
    if (me?.type === AccountType.ADMIN || me?.type === AccountType.LOC_ADMIN) {
      return true;
    }

    return (
      me?.type === AccountType.ORG_ADMIN &&
      me?.organization?.org_feature_use_limit.some(
        (item) =>
          item.feature.name === OrgFeatures.USE_LOCAL_POST && item.can_use
      )
    );
  }, [me?.organization?.org_feature_use_limit, me?.type]);

  const { locationRecommendations } = useLocationRecommendations(
    locationId,
    organizationId
  );

  const { recentLocalPostList } = useLocationLocalPostsList(locationId);

  const shouldShowReviewSummaries =
    me?.type === AccountType.ADMIN ||
    FeatureUseLimitCalculator.getFeatureUseStatus(
      LocFeatures.VIEW_REVIEW_SUMMARIES,
      locationData?.commonFeatureUseLimit
    ) === FeatureUseStatus.CAN_USE;

  // Get all organization IDs for this location
  const organizationIds = useMemo(() => {
    if (
      !locationData?.organizations ||
      locationData.organizations.length === 0
    ) {
      return undefined;
    }
    return locationData.organizations.map((org) => String(org.id));
  }, [locationData?.organizations]);

  // Fetch location's project data for onboarding banners
  const { locationProjectsData } = useLocationProjects(
    organizationIds,
    locationId
  );

  return (
    <>
      <InternalToolsContextProvider
        defaultTools={internalToolsInUse}
        toolsToExpose={internalToolsInUse}
      >
        <div className="right-content facility-details">
          <div className="back-top-bar flex-grid">
            <NavLink
              to={produceBackLink(organizationId, me)}
              className="back-link"
            >
              <i className="icons icon-back"></i>
              {t("back_to_list")}
            </NavLink>
            <div className="right-btns">
              {isOrgUser() &&
                locationData &&
                (locationData.has_loc_admins ? (
                  <button
                    onClick={() => {
                      setShowSendMessageScreens(true);
                    }}
                    className="btn btn-blue"
                  >
                    {t("to_send_message_to_fac")}
                  </button>
                ) : (
                  <div className="btn btn-blue btn-blue--fade">
                    {t("to_send_message_to_fac")}
                    <div className="pink-tips">
                      {t("cannot_send_because_no_loc_admin")}
                    </div>
                  </div>
                ))}
              {!!gbpLocIdNum && (
                <div className="to-gbp-button">
                  <a
                    href={`https://business.google.com/n/${gbpLocIdNum}/profile`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-blue"
                    onClick={() => {
                      ReactGA.event({
                        category: "GBP Link",
                        action: "Click to GBP",
                        label: "main",
                      });
                    }}
                  >
                    {t("to_facilitys_gbp_profile")}
                  </a>
                  <small className="footnote">
                    {t("basicChecklist.may_require_gbp_login")}
                  </small>
                </div>
              )}
            </div>
          </div>
          {!!locationData && (
            <div className="flex-grid">
              <div className="big-bold-title facility-details-title">
                <PageTitle text={locationData.name} />
                {locationData.is_demo && <DemoBadge type="facility" />}
              </div>
              <div className="rights">
                <div className="big-bold-title">
                  {t("health_index")}
                  <ScoreItem score={locationData.stat.score} />
                </div>
                <div className="details-preferences-link-container">
                  <NavLink
                    className="details-preferences-link"
                    to={`/location/${locationId}/preferences`}
                  >
                    {t("details_and_preferences")}
                  </NavLink>
                </div>
              </div>
            </div>
          )}
          {locationProjectsData && locationProjectsData.length > 0 && (
            <div className="detail-group">
              <div className="group-title">
                {t("on_boarding")}
                <div className="line"></div>
              </div>
              {locationProjectsData.map(
                (projectData: ProjectLocationWithProject) => (
                  <OnboardingBanner
                    key={`${projectData.project_id}-${projectData.location_id}`}
                    projectLocationData={projectData}
                  />
                )
              )}
            </div>
          )}
          {!!locationData && (
            <>
              <div className="detail-group">
                <div className="group-title">
                  {t("basic_profile")}
                  <div className="line"></div>
                </div>
                <BasicProfile
                  organizationId={organizationId}
                  locationData={locationData}
                  onClickReviewCounts={() => {
                    internalToolsRef.current?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                  onOpenModalEditProfile={setModalEditProfileSectionKey}
                />
              </div>

              {!(
                isOrgUser() &&
                !me?.organization?.org_feature_use_limit.some(
                  (item) =>
                    item.feature.name === OrgFeatures.USE_LOCAL_POST &&
                    item.can_use
                )
              ) && (
                <div className="detail-group">
                  <div className="group-title">
                    {t("external_integrations")}
                    <div className="line"></div>
                  </div>
                  <ExternalIntegrations locationData={locationData} />
                </div>
              )}

              <div className="detail-group">
                <div className="group-title">
                  {t("report")}
                  <div className="line"></div>
                </div>

                <Summary
                  statsPrime={locationData}
                  healthIndex={locationData ? locationData.stat?.score : ""}
                  profileViewsChangeRatio={profileViewsChangeRatio}
                  pageType="FacilityDetails"
                />

                {!!locationRecommendations && (
                  <Suggestions
                    buttonType={"link"}
                    shownAll={false}
                    recommendations={locationRecommendations}
                  />
                )}
                <ActivityLog
                  locationId={locationId}
                  locationName={locationName}
                />
                {shouldShowReviewSummaries && (
                  <ReviewSummary locationData={locationData} />
                )}
                <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
                  <ReviewRequestPrint locationData={locationData} />
                </OptionalRender>
                <RecentReviewsSection
                  locationId={locationId}
                  organizationId={organizationId}
                />

                {!keywordAnalysisLoading &&
                  latestKeywordAnalysis &&
                  latestKeywordAnalysis.analysis_data?.top_keywords?.length >
                    0 && (
                    <KeywordAnalysisCard
                      keyword={
                        latestKeywordAnalysis.analysis_data.top_keywords[0]
                          .keyword
                      }
                      searchCountTotal={
                        latestKeywordAnalysis.analysis_data.top_keywords[0]
                          .search_count_total
                      }
                      combinedScore={
                        latestKeywordAnalysis.analysis_data.top_keywords[0]
                          .combined_score
                      }
                      reviewMentionCount={
                        latestKeywordAnalysis.analysis_data.top_keywords[0]
                          .review_mention_count
                      }
                      reason={
                        latestKeywordAnalysis.analysis_data.top_keywords[0]
                          .reason
                      }
                      showDetailButton={true}
                      onDetailClick={() => {
                        history.push(
                          `/location/${locationId}/keyword-analysis/${latestKeywordAnalysis.id}`
                        );
                      }}
                    />
                  )}

                <div className="two-panel">
                  <div className="row">
                    <div className="col col-md-5">
                      <BasicChecklist
                        onOpenModalUnRepliedReviews={() =>
                          setShowUnRepliedReviews(true)
                        }
                        locationData={locationData}
                        onClickCreatePostToGoogleBusinessProfile={() =>
                          setShownModalCreateLocalPost(true)
                        }
                        onOpenModalEditProfile={setModalEditProfileSectionKey}
                      />
                    </div>
                    <div className="col col-md-7">
                      <ImportantMetrics locationData={locationData} />
                    </div>
                  </div>
                </div>
                <PickupFields
                  locationData={locationData}
                  onOpenModalEditProfile={setModalEditProfileSectionKey}
                />
              </div>
              <div className="detail-group">
                <div className="group-title">
                  {t("recent_local_posts_of_facility")}
                  <div className="line"></div>
                </div>
                <small className="status-hint">
                  {t("differs_from_actual_status")}
                </small>
                <small className="cant-find-post">
                  {_t("dashboardPage.localPostList.cant_find_post")}
                </small>
                <div className="recent-local-posts-container">
                  <RecentPostList
                    dataList={recentLocalPostList?.slice(0, 5)}
                    isShowLocationName={false}
                    context={RecentMediaListContext.OWN_LOCATION_HISTORY}
                  />
                  <div className="local-posts-button-group">
                    {recentLocalPostList && (
                      <button
                        className="btn btn-border"
                        onClick={() => {
                          ReactGA.event({
                            category: "facility_details",
                            action: "View More Local Posts",
                          });
                          history.push(`/location/${locationId}/localposts`);
                        }}
                      >
                        {t("view_more")}
                      </button>
                    )}
                    {canCreateLocalPost && (
                      <button
                        className="btn btn-blue btn-new-post"
                        onClick={() => {
                          setShownModalCreateLocalPost(true);
                          ReactGA.event({
                            category: "facility_details",
                            action: "Create New Local Post",
                          });
                        }}
                      >
                        <Pencil
                          size={16}
                          color="white"
                          className="pencil-icon"
                        />
                        {t("new_post")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="detail-group">
                <div className="group-title">
                  {t("recent_media_uploads_of_facility")}
                  <div className="line"></div>
                </div>
                <RecentMediaUploadsSection locationId={locationId} />
              </div>
              <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
                <div className="detail-group">
                  <div className="group-title">
                    {t("media_catalogue_of_facility")}
                    <div className="line"></div>
                  </div>
                  <small className="status-hint">
                    {t("media_catalogue_description")}
                  </small>
                  <MediaCatalogueSection locationId={locationId} />
                </div>
              </OptionalRender>
              <FacilityDetailsAnalysis
                locationId={locationId}
                organizationId={organizationId}
                facilityName={locationData?.name}
              />
              <div className="detail-group">
                <div className="group-title">
                  {t("search_keywords")}
                  <div className="line"></div>
                </div>
                <div className="white-border-panel keywords-module padding32">
                  {keywordData && keywordData.length > 0 ? (
                    <LocationKeywordsChart
                      data={keywordData}
                      height={400}
                      width="100%"
                      onDateRangeChange={handleKeywordDateRangeChange}
                      canSelectionMultiLocations={false}
                      initLocationIds={locationData ? [locationData.id] : []}
                      initDateRange={calculateInitialDateRange}
                      organizationId={organizationId}
                      locationId={locationId}
                      facilityName={locationData?.name}
                    />
                  ) : (
                    <div className="no-data-container">
                      <div className="txt">
                        {t("no_keyword_data_available")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <FacilityDataDownload locationData={locationData} />
            </>
          )}
        </div>
      </InternalToolsContextProvider>
      {locationData && showSendMessageScreens && (
        <SendMessageScreens
          locationData={locationData}
          setShowSendMessageScreens={setShowSendMessageScreens}
        />
      )}

      {locationData && shownModalCreateLocalPost && (
        <ModalCreateLocalPost
          locationData={locationData}
          locationId={locationId}
          onClose={() => {
            setShownModalCreateLocalPost(false);
            refresh(RefreshType.LOCATION, RefreshType.LOCAL_POSTS);
          }}
        />
      )}
      {showUnRepliedReviews && (
        <ModalUnRepliedReviews
          locationId={locationId}
          onClose={() => setShowUnRepliedReviews(false)}
        />
      )}
      {modalEditProfileSectionKey !== undefined && !!locationData && (
        <ConfirmCloseContextProvider>
          <ModalEditProfile
            locationData={locationData}
            onClose={() => setModalEditProfileSectionKey(undefined)}
            defaultAccordionEventKey={modalEditProfileSectionKey}
          />
        </ConfirmCloseContextProvider>
      )}
    </>
  );
};

export default withRouter(FacilityDetailsPage);
